import type { AIFindingCategory, AIFindingSeverity } from "./ai-review-model.js";
import { AI_CATEGORY_ORDER } from "./ai-review-model.js";
import { preservesProtectedTokens } from "./protected-tokens.js";

/**
 * NÃO CONFIAR EM JSON DA IA SEM VALIDAR.
 *
 * Toda resposta de provedor passa por aqui antes de virar finding. Estrutura
 * inválida é recusada (o orquestrador tenta de novo, com limite, e depois
 * marca a revisão como FALHOU) — nunca é salva pela metade.
 */
export interface ProviderFinding {
  readonly fieldPath: string;
  readonly category: AIFindingCategory;
  readonly severity: AIFindingSeverity;
  readonly originalText: string;
  readonly suggestedText: string;
  readonly explanation: string;
  readonly autoApplicable: boolean;
  readonly ruleId: string;
  readonly justifiable: boolean;
  readonly offset: number;
}

const SEVERITIES: readonly AIFindingSeverity[] = ["INFO", "LINGUISTICO", "TERMINOLOGICO", "TECNICO", "CRITICO"];

export type SchemaResult = { readonly ok: true; readonly findings: ProviderFinding[] } | { readonly ok: false; readonly error: string };

export function validateFindingsOutput(raw: unknown, knownFieldPaths: ReadonlySet<string>): SchemaResult {
  if (!raw || typeof raw !== "object" || !Array.isArray((raw as { findings?: unknown }).findings)) {
    return { ok: false, error: "Resposta sem a lista \"findings\"." };
  }
  const out: ProviderFinding[] = [];
  for (const [i, f] of ((raw as { findings: unknown[] }).findings).entries()) {
    if (!f || typeof f !== "object") return { ok: false, error: `findings[${i}] não é objeto.` };
    const o = f as Record<string, unknown>;
    const str = (k: string) => (typeof o[k] === "string" ? (o[k] as string) : null);
    const fieldPath = str("fieldPath");
    const category = str("category") as AIFindingCategory | null;
    const severity = str("severity") as AIFindingSeverity | null;
    const originalText = str("originalText");
    const suggestedText = str("suggestedText");
    const explanation = str("explanation");
    if (fieldPath === null || originalText === null || suggestedText === null || !explanation) return { ok: false, error: `findings[${i}] com campo obrigatório ausente.` };
    if (!category || !AI_CATEGORY_ORDER.includes(category)) return { ok: false, error: `findings[${i}].category inválida.` };
    if (!severity || !SEVERITIES.includes(severity)) return { ok: false, error: `findings[${i}].severity inválida.` };
    if (fieldPath !== "" && !knownFieldPaths.has(fieldPath)) return { ok: false, error: `findings[${i}].fieldPath desconhecido (${fieldPath}).` };
    if (typeof o["autoApplicable"] !== "boolean") return { ok: false, error: `findings[${i}].autoApplicable ausente.` };
    // Travas que nenhum provedor contorna: só linguístico é automático, e
    // só quando preserva todos os valores protegidos.
    const safe = o["autoApplicable"] === true && severity === "LINGUISTICO" && suggestedText !== "" && preservesProtectedTokens(originalText, suggestedText);
    out.push({
      fieldPath, category, severity, originalText, suggestedText, explanation,
      autoApplicable: safe,
      ruleId: str("ruleId") || "PROVIDER",
      justifiable: o["justifiable"] === true && severity === "TECNICO",
      offset: typeof o["offset"] === "number" ? (o["offset"] as number) : -1
    });
  }
  return { ok: true, findings: out };
}
